// "My Results" ecosystem plan, Phase 1 (core page), Stage 1. Reads GET
// /my-results (backend, this same app) — a new, additive route alongside
// /my-summaries (completely untouched by this). Shows each test value
// plotted against its own reference range, with a plain-language
// explanation for anything flagged abnormal.
//
// Deliberately NOT a visual clone of any other lab portal — built from
// this app's own existing brand (navy/gold, MUI) with one load-bearing
// device of its own: the range-position gauge bar. Clicking a value opens
// a MODAL (not an inline expand) — the list stays compact and scannable;
// the modal is the one place detail (explanation, FAQs, and later the
// trend sparkline + per-value AI chat) lives, per direct design feedback
// mid-build. See /Users/abhaybhaskarmallampalli/Downloads/proovereport/
// MY_RESULTS_ECOSYSTEM_PLAN.md §5 for the full staged plan (Stage 2 —
// trends; Stage 3 — AI chat) this page's reserved layout slots anticipate.
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import {
  Box, Typography, CircularProgress, Chip, Alert, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, IconButton, Tooltip, TextField,
} from "@mui/material";
import {
  Science, ArrowBack, Close, ExpandMore, LocalFlorist, Assignment, PersonOutline, Send,
  Biotech, Medication, WarningAmber, HourglassEmpty, SmartToy,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import UserContext from "./UserContext";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import { formatEST } from "../dateUtils";
import { displayNameForTestId } from "../testDisplayNames";
import TrendsPanel, { buildDatasets, chartOptions } from "./TrendsPanel";
import { Line } from "react-chartjs-2";
import ReactMarkdown from "react-markdown";
import { extractErrorDetail } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const MY_RESULTS_ENABLED = process.env.REACT_APP_MY_RESULTS_ENABLED === "true";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };
// Status colors — 2026-10-21, matched to MySummaries.js's own existing
// abnormal/normal convention by direct user decision (the same signal
// must read the same color regardless of which page a patient is on,
// not a page-specific palette). #cc0000 / #4ade80(dark)/#1a7a1a(light)
// are MySummaries.js's exact values (see its FlaggedBadge/per-row color
// logic) — reused verbatim, not reinvented. "Critical" gets its own
// deeper red, still clearly in the same alarm family, since this page
// (unlike My Summaries) distinguishes a flagged value from a critical one.
const STATUS = {
  normal:   { label: "In range" },
  high:     { label: "High" },
  low:      { label: "Low" },
  critical: { label: "Critical" },
  // 2026-10-21 — added for Proove/PGx "level"/"phenotype" text (risk
  // levels and genotype-driven findings), which has no abnormal/normal
  // binary the way a lab value does. See proveLevelStatusKey below.
  moderate:      { label: "Moderate" },
  indeterminate: { label: "Indeterminate" },
};
function statusColor(statusKey, isDark) {
  if (statusKey === "normal") return isDark ? "#4ade80" : "#1a7a1a";
  if (statusKey === "critical") return "#991b1b";
  if (statusKey === "moderate") return "#b45309";
  if (statusKey === "indeterminate") return isDark ? "#94a3b8" : "#64748b";
  return "#cc0000"; // high/low
}

// 2026-10-21 fix (downstream-mismatch-checker finding) — this used to
// only recognize critical/high/low, but the backend's own definition of
// "abnormal" for a standard-shape row (ocr_engine.py's abnormal_statuses,
// mirrored by main.py's _standard_shape_flagged) also includes "Abnormal"
// and "Detected" (e.g. a positive pathogen result on a PCR panel). Those
// were silently falling through to "normal" — a green chip and excluded
// from the abnormal-only filter, directly contradicting the server's own
// flagged_count. Matched here, not deferred to the server's flagged list,
// since this function also drives the gauge's color, not just the filter.
function statusKeyFor(status, override) {
  // `override` lets a caller hand in a precomputed key (e.g.
  // proveLevelStatusKey's result for a Proove risk/genotype row) instead
  // of running this lab-value-vocabulary matcher over text it was never
  // designed to classify — see GaugeRow/ValueDetailModal's own calls.
  if (override) return override;
  const s = String(status || "").toLowerCase();
  if (s.includes("critical")) return "critical";
  if (s.includes("high")) return "high";
  if (s.includes("low")) return "low";
  if (s.includes("abnormal") || s.includes("detected")) return "high";
  return "normal";
}

// Proove/PGx "level"/"phenotype" text -> this page's status-chip buckets.
// Priority order ported from MySummaries.js's own bandIndexForLevel
// (compound phrases checked before bare words) so the same level text
// reads the same severity color on both pages. Falls back to
// "indeterminate" (neutral gray) rather than guessing a severity for
// text this doesn't recognize — a plain genotype code like "C/C" must
// never accidentally render as an amber "Moderate" warning.
function proveLevelStatusKey(level) {
  const l = String(level || "").trim().toLowerCase();
  if (!l) return "indeterminate";
  if (l.includes("indeterminate") || l.includes("not called") || l.includes("ambiguous") || l.includes("inconclusive")) return "indeterminate";
  // 2026-10-21 fix (downstream-mismatch-checker finding, CONFIRMED) — this
  // used to collapse "moderately/mildly low" AND "moderately/mildly high"
  // into one shared "moderate" bucket. MySummaries.js's own
  // bandIndexForLevel keeps them split (low->band0/green, high->band2/
  // red), so the SAME stats_json level text (e.g. "Moderately Low") was
  // reading green on My Summaries and amber on My Results for the same
  // patient, same value. Priority order below now matches
  // bandIndexForLevel exactly for every word that function also handles.
  if (l.includes("moderately low") || l.includes("mildly low")) return "normal";
  if (l.includes("moderately high") || l.includes("mildly high")) return "high";
  if (l.includes("high") || l.includes("avoid") || l.includes("alternative") || l.includes("hps") || l.includes("poor") || l.includes("decreased function")) return "high";
  if (l.includes("moderate") || l.includes("further evaluation") || l.includes("caution") || l.includes("aps") || l.includes("intermediate")) return "moderate";
  // "abnormal" must be checked before the bare "normal" substring match
  // below — a level literally phrased "Abnormal" would otherwise match
  // `.includes("normal")` and render as a reassuring green "In range".
  if (l.includes("abnormal")) return "indeterminate";
  if (l.includes("low") || l.includes("standard") || l.includes("no genetic predisposition") || l.includes("lps") || l.includes("normal") || l.includes("extensive") || l.includes("increased function")) return "normal";
  return "indeterminate";
}

// Which stats_json score fields get a tile, per report_type_id —
// labariumaireport's own proove_test_id values. Ported verbatim from
// MySummaries.js's own STAT_LABELS (same shapes, {score, level} per
// _typed_normalize_extraction) so the same fields surface on both pages.
const PROOVE_SCORE_LABELS = {
  proove_opioid_risk: [
    { field: "proove_ori", label: "Opioid Risk Index" },
    { field: "ort", label: "Opioid Risk Tool (ORT)" },
    { field: "phq2", label: "PHQ-2" },
  ],
  proove_pain_perception: [
    { field: "self_reported_pain", label: "Self-Reported Pain" },
    { field: "pss", label: "Perceived Stress Scale" },
  ],
};

// ── Reference-range parsing — best-effort, degrades gracefully ──────────
// Handles "70-100", "70 - 100", "70-100 mg/dL", "<5", ">10", ">=10", "≤5".
// Anything else (e.g. "Negative", "Not Detected") returns null — the row
// then renders a plain status chip instead of a gauge, never a broken/
// fake bar. 2026-10-21 fix (ui-ux-checker finding) — un-anchored the end
// of each pattern (no trailing `$`) so an optional unit suffix some lab
// configs append directly to this field (e.g. "70-100 mg/dL") still
// parses, instead of silently dropping to no-gauge.
function parseRange(referenceRange) {
  const s = String(referenceRange || "").trim();
  if (!s) return null;
  const dash = s.match(/^(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)/);
  if (dash) return { low: parseFloat(dash[1]), high: parseFloat(dash[2]) };
  const lt = s.match(/^[<≤]=?\s*(-?\d+(?:\.\d+)?)/);
  if (lt) return { low: null, high: parseFloat(lt[1]) };
  const gt = s.match(/^[>≥]=?\s*(-?\d+(?:\.\d+)?)/);
  if (gt) return { low: parseFloat(gt[1]), high: null };
  return null;
}

function parseValue(value) {
  // 2026-10-21 fix (ui-ux-checker finding) — strip thousands separators
  // first (e.g. a platelet/WBC count like "12,000") so this doesn't
  // silently truncate at the comma and plot a wildly wrong position
  // against a status chip that's computed server-side from the real
  // number.
  const m = String(value ?? "").trim().replace(/,/g, "").match(/^-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
}

// Gauge geometry: a padded display window around [low, high] so the
// marker never sits exactly at the track's hard edge even when the value
// is right at the boundary. Falls back to null (no gauge rendered) when
// either the range or the value itself isn't numeric.
function gaugeGeometry(referenceRange, value) {
  const range = parseRange(referenceRange);
  const v = parseValue(value);
  if (!range || v === null) return null;
  const { low, high } = range;
  if (low === null && high === null) return null;
  const span = (high ?? low) - (low ?? high) || Math.abs(high ?? low) || 1;
  const pad = span * 0.4;
  const windowLow = (low ?? high - span) - pad;
  const windowHigh = (high ?? low + span) + pad;
  const rawPct = ((v - windowLow) / (windowHigh - windowLow)) * 100;
  const pct = (n) => Math.max(0, Math.min(100, ((n - windowLow) / (windowHigh - windowLow)) * 100));
  return {
    markerPct: pct(v),
    bandStartPct: low !== null ? pct(low) : 0,
    bandEndPct: high !== null ? pct(high) : 100,
    // 2026-10-21 fix (ui-ux-checker finding) — every value clamped past
    // the window's edge collapsed to the identical marker position (a
    // mildly-high 115 looked visually identical to a wildly-high 1000).
    // Surfaced so GaugeRow can draw a small overflow chevron instead of
    // silently pinning the marker.
    clampedBeyond: rawPct > 100 ? "high" : rawPct < 0 ? "low" : null,
  };
}

// ── Gauge row — the one load-bearing visual device on this page ─────────
// 2026-10-21 fixes (ui-ux-checker findings):
// - Row now wraps on mobile (flexWrap at xs) instead of a 100%-width,
//   non-shrinking label column racing other non-shrinking siblings in a
//   nowrap flex row — that combination mathematically can't fit and was
//   pushing the chip/dot off-screen on a real phone width.
// - Gauge track column is only rendered at all when real geometry exists
//   (no more empty flex:1 box leaving a blank gap on qualitative rows).
// - A small chevron now marks a value clamped past the window edge, so a
//   mildly-high and a wildly-high value no longer look identical.
// - The "explanation available" affordance uses a real MUI Tooltip (this
//   app's own established pattern — LabReports.js etc.), and the row is
//   keyboard-reachable (role="button", tabIndex, Enter/Space) rather than
//   a click-only Box.
function GaugeRow({ row, insight, onOpen, isDark }) {
  const statusKey = statusKeyFor(row.status, row._statusKey);
  const color = statusColor(statusKey, isDark);
  const geometry = gaugeGeometry(row.reference_range, row.value);
  const open = () => onOpen(row, insight);

  return (
    <Box
      onClick={open}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
      sx={{
        display: "flex", flexWrap: { xs: "wrap", sm: "nowrap" }, alignItems: "center", gap: 1.5,
        py: 1.4, px: 1.5, borderRadius: "10px", cursor: "pointer", minWidth: 0,
        "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.03)" },
        "&:focus-visible": { outline: `2px solid ${BRAND.gold}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ minWidth: 0, width: { xs: "100%", sm: 170 }, flexShrink: 0 }}>
        <Typography sx={{
          fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {row.test}
        </Typography>
        <Typography sx={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b" }}>
          {row.value} {row.unit || ""}
          {/* Mobile has no gauge track (no room at phone width) — show
              the reference range in text instead of losing it entirely. */}
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}> &nbsp;· Ref: {row.reference_range || "—"}</Box>
        </Typography>
      </Box>

      {geometry && (
        <Box sx={{ flex: "1 1 160px", minWidth: 0, display: { xs: "none", sm: "block" } }}>
          <Box sx={{ position: "relative", height: 8, borderRadius: "4px", bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.08)" }}>
            <Box sx={{
              position: "absolute", top: 0, bottom: 0,
              left: `${geometry.bandStartPct}%`, width: `${Math.max(2, geometry.bandEndPct - geometry.bandStartPct)}%`,
              bgcolor: isDark ? "rgba(46,125,50,0.35)" : "rgba(46,125,50,0.18)", borderRadius: "4px",
            }} />
            <Box sx={{
              position: "absolute", top: -3, width: 14, height: 14, borderRadius: "50%",
              left: `calc(${geometry.markerPct}% - 7px)`, bgcolor: color,
              border: `2px solid ${isDark ? "#0b1220" : "#fff"}`, boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            }} />
            {geometry.clampedBeyond && (
              <Box sx={{
                position: "absolute", top: -2, [geometry.clampedBeyond === "high" ? "right" : "left"]: -4,
                fontSize: 10, fontWeight: 900, color, lineHeight: 1,
              }}>
                {geometry.clampedBeyond === "high" ? "»" : "«"}
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Chip size="small" label={STATUS[statusKey].label} sx={{
        fontSize: 11, fontWeight: 700, flexShrink: 0,
        bgcolor: `${color}1a`, color,
      }} />
      {insight && (
        <Tooltip title="Explanation available — tap to view" enterDelay={300}>
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: BRAND.gold, flexShrink: 0 }} />
        </Tooltip>
      )}
    </Box>
  );
}

// ── Detail modal — the dedicated space for everything a click reveals ───
// 2026-10-21 fix (ui-ux-checker finding) — this used to bail out with
// `if (!row) return null`, which unmounts the Dialog's own JSX the
// instant `row` goes null (the same render pass that sets `open` to
// false) — before MUI's own exit transition can play, so the dialog
// snapped shut instantly instead of fading out. Holds onto the last
// non-null row via a ref so the Dialog's content stays renderable
// through its close animation; `open` alone still controls visibility.
function ValueDetailModal({ open, onClose, row: liveRow, insight: liveInsight, isDark, trendChart: liveTrendChart }) {
  const lastRef = React.useRef({ row: liveRow, insight: liveInsight, trendChart: liveTrendChart });
  if (liveRow) lastRef.current = { row: liveRow, insight: liveInsight, trendChart: liveTrendChart };
  const { row, insight, trendChart } = lastRef.current;
  if (!row) return null;
  const statusKey = statusKeyFor(row.status, row._statusKey);
  const color = statusColor(statusKey, isDark);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, bgcolor: isDark ? "#111827" : "#fff" } } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: isDark ? "#f1f5f9" : BRAND.navy }}>{row.test}</Typography>
          <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", mt: 0.3 }}>
            {row.value} {row.unit || ""} &nbsp;·&nbsp; Reference: {row.reference_range || "—"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: isDark ? "#94a3b8" : "#64748b" }}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Chip size="small" label={STATUS[statusKey].label} sx={{ fontWeight: 700, bgcolor: `${color}1a`, color, mb: 2 }} />
        {insight ? (
          <>
            {insight.what_it_is && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.4, textTransform: "none" }}>What this measures</Typography>
                <Typography sx={{ fontSize: 14, color: isDark ? "#e5e7eb" : "#1e293b", lineHeight: 1.6 }}>{insight.what_it_is}</Typography>
              </Box>
            )}
            {insight.what_it_means_for_you && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.4 }}>What this may mean for you</Typography>
                <Typography sx={{ fontSize: 14, color: isDark ? "#e5e7eb" : "#1e293b", lineHeight: 1.6 }}>{insight.what_it_means_for_you}</Typography>
              </Box>
            )}
            {Array.isArray(insight.faqs) && insight.faqs.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.8 }}>Common questions</Typography>
                {insight.faqs.map((faq, i) => (
                  <Box key={i} sx={{ mb: 1.2 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b", display: "flex", alignItems: "flex-start", gap: 0.6 }}>
                      <ExpandMore sx={{ fontSize: 16, mt: 0.2, color: BRAND.gold, flexShrink: 0 }} />
                      {faq.question}
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: isDark ? "#94a3b8" : "#64748b", ml: 2.8, lineHeight: 1.55 }}>{faq.answer}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: 13.5, color: isDark ? "#94a3b8" : "#64748b" }}>
            {statusKey === "normal"
              ? "This value is within its expected range — no further explanation needed."
              : "No explanation is available for this value yet."}
          </Typography>
        )}
        {/* 2026-10-21 fix (ui-ux-checker finding) — these reserved slots
            used to sit inside the `insight ?` branch above, so a normal
            (non-flagged) value could never show a trend line or open a
            chat even though a patient may reasonably want either for a
            value that's always been normal. Rendered unconditionally
            here instead, regardless of whether an AI explanation exists
            for this specific value. */}
        {/* Stage 2 — per-value sparkline, filled in. Reuses TrendsPanel.js's
            own buildDatasets/chartOptions (exported for exactly this reuse)
            so the reference-range dashed-line logic isn't duplicated. Only
            renders when this test has 2+ history points — same inclusion
            rule the backend already applied to trends.charts. */}
        {trendChart && (
          <Box sx={{ mt: 2, pt: 2, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.06)" }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 1 }}>
              Trend across your {trendChart.points.length} saved results
            </Typography>
            <Box sx={{ position: "relative", height: 160 }}>
              <Line
                data={{ labels: trendChart.points.map((p) => p.date_label), datasets: buildDatasets(trendChart, {
                  High: statusColor("high", isDark), Low: statusColor("low", isDark), Normal: statusColor("normal", isDark),
                }) }}
                options={chartOptions(trendChart, isDark)}
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              />
            </Box>
          </Box>
        )}
        {/* Stage 3 — per-value inline chat, filled in. `key={row.test}`
            on the wrapper forces a fresh mount (and so a fresh,
            empty conversation) whenever a different value is opened —
            per the plan's own "never persisted, lives only in that page
            view" rule, nothing here survives past this one value. */}
        <ValueChatBox key={row.test} row={row} insight={insight} isDark={isDark} />
      </DialogContent>
    </Dialog>
  );
}

// Stage 3 — small inline chat, seeded with only this ONE value's own
// context. Multi-turn within this modal's own lifetime: each turn's
// running message array is passed back to the backend so follow-ups have
// real context, same as any ordinary chat UI — but nothing here is ever
// written to a database; it's plain React state, gone the moment this
// modal closes or a different value is opened (the `key` prop above).
function ValueChatBox({ row, insight, isDark }) {
  const { user } = useContext(UserContext);
  const token = user?.token;
  const [messages, setMessages] = useState([]); // [{question, answer}]
  const [question, setQuestion] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const inputRef = React.useRef(null);
  const bottomRef = React.useRef(null);

  // 2026-10-21 fix (ui-ux-checker finding) — scroll the newest turn into
  // view automatically; previously a 220px fixed-height inner scroller
  // had no auto-scroll at all, so a new answer could land below the
  // fold of its OWN nested scroll region, inside the dialog's own
  // separate scroll region — a double-scroll a user might not even
  // register as scrollable. Removed the fixed-height inner box entirely
  // (DialogContent is now the one scroll region) and scroll the latest
  // turn into view instead.
  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages]);

  const send = async () => {
    const q = question.trim();
    if (!q || sending) return;
    setSending(true);
    setError("");
    try {
      const res = await axios.post(`${API_BASE}/my-results/ask-about-value`,
        { question: q, history: messages, value_context: {
          test: row.test, value: row.value, unit: row.unit, reference_range: row.reference_range, status: row.status,
          // 2026-10-21 fix (downstream-mismatch-checker finding) — the
          // backend's prompt builder has a richer branch for exactly
          // these two fields (what the test measures / what this value
          // may mean for this patient); they were never sent, so that
          // branch was permanently dead and every answer came from a
          // materially thinner context than intended.
          what_it_is: insight?.what_it_is, what_it_means_for_you: insight?.what_it_means_for_you,
        } },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // 2026-10-24 fix (carried-forward finding #2, flow audit) — the
      // backend now returns a clean {feature_disabled: true} shape
      // instead of a bare 404 when this feature is off; check for it
      // before treating the response as a real answer.
      if (res.data?.feature_disabled) {
        setError("This feature isn't available yet.");
        return;
      }
      setMessages((prev) => [...prev, { question: q, answer: res.data.answer }]);
      setQuestion("");
    } catch (err) {
      // 2026-10-21 fix (downstream-mismatch-checker finding) — this used
      // to always show the same generic message regardless of WHY the
      // call failed (a too-long question, rate limit, or a genuine
      // server error all looked identical); now surfaces the real
      // backend detail, same `extractErrorDetail` pattern BotInterface.js
      // already uses for every other AI-answer failure in this app.
      setError(extractErrorDetail(err, "Couldn't get an answer right now — please try again."));
    } finally {
      setSending(false);
      // 2026-10-21 fix (ui-ux-checker finding) — disabling a currently-
      // focused TextField moves focus to <body> (standard browser
      // behavior); nothing previously restored it once re-enabled, so a
      // keyboard-only user lost focus after every single message and had
      // to Tab back in to ask a follow-up. Restored here, after the
      // field is editable again.
      inputRef.current?.focus();
    }
  };

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.06)" }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.4 }}>
        Ask about this value
      </Typography>
      {/* 2026-10-21 fix (ui-ux-checker finding, trust-relevant) — a
          persistent, always-visible disclaimer (not just a one-time
          empty-state hint) so a reassuring, confidently-worded answer is
          never mistaken for something written by clinical staff. */}
      <Typography sx={{ fontSize: 11.5, color: isDark ? "#64748b" : "#94a3b8", mb: 1.2, lineHeight: 1.5 }}>
        Answers are generated by AI based on this one result — not a substitute for advice from your provider.
      </Typography>
      {messages.length > 0 && (
        <Box sx={{ mb: 1.5, display: "flex", flexDirection: "column", gap: 1.2 }}>
          {messages.map((m, i) => (
            <Box key={i}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b" }}>{m.question}</Typography>
              {/* 2026-10-21 fix (downstream-mismatch-checker finding) —
                  the model's own answer is markdown (the prompt itself
                  is authored in markdown, same as every other
                  _vertex_ask-sourced answer in this app), and every
                  sibling consumer (BotInterface.js) renders it through
                  ReactMarkdown, not plain text — rendering it as plain
                  Typography showed literal "**bold**"/"- " characters
                  instead of formatted text. Matched to the sibling FAQ
                  answer's own treatment (13.5px, muted color) since both
                  are "explanatory AI text" inside this same modal. */}
              <Box sx={{
                fontSize: 13.5, color: isDark ? "#94a3b8" : "#64748b", mt: 0.3, lineHeight: 1.55,
                "& p": { m: 0, mb: 0.8 }, "& ul": { m: 0, pl: 2.5 },
              }}>
                <ReactMarkdown>{m.answer}</ReactMarkdown>
              </Box>
            </Box>
          ))}
          <div ref={bottomRef} />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, fontSize: 13 }}>{error}</Alert>}
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField size="small" fullWidth placeholder="e.g. What could cause this?" value={question}
          inputRef={inputRef}
          slotProps={{ input: { readOnly: sending } }}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <IconButton onClick={send} disabled={sending || !question.trim()} aria-label="Send question"
          sx={{ color: BRAND.navy, "&:hover": { bgcolor: "rgba(0,53,122,0.08)" } }}>
          {sending ? <CircularProgress size={18} sx={{ color: BRAND.navy }} /> : <Send fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );
}

// report_metadata is a LIST of {field, value} OCR records (confirmed
// against ocr_engine.py's extract_metadata/get_demographic_value — NOT a
// dict with fixed semantic keys), and the exact field LABEL text is
// per-lab-config (age_field/sex_field/etc. are each configurable per
// report type, no single universal label guaranteed). Best-effort
// substring match, case-insensitive; returns "" (never throws, never
// shows "undefined") when nothing matches.
function metaFieldLike(reportMetadata, ...substrings) {
  if (!Array.isArray(reportMetadata)) return "";
  const hit = reportMetadata.find((row) => {
    const field = String(row?.field || "").toLowerCase();
    return substrings.some((s) => field.includes(s));
  });
  return hit?.value || "";
}

// 2026-10-21 fix (ui-ux-checker finding) — MySummaries.js's own
// IdentityHeader explicitly calls this a "mandatory... visual safeguard"
// (a tinted box so it registers as a distinct "this is who this belongs
// to" strip at a glance, not just inline text). This page had dropped
// that tinted treatment — restored here, same bgcolor/borderRadius/
// padding convention as the sibling page, with this page's own more
// relevant fields (collection date, ordering physician) rather than
// copying upload-metadata fields that don't apply here.
function DemographicsStrip({ result, isDark }) {
  const meta = result.report_metadata;
  const physician = metaFieldLike(meta, "physician", "provider", "ordering");
  return (
    <Box sx={{
      display: "flex", flexWrap: "wrap", gap: 2, mb: 2, px: 1.5, py: 1,
      borderRadius: 1.5, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.04)",
      fontSize: 13, color: isDark ? "#94a3b8" : "#64748b",
    }}>
      <span><b style={{ color: isDark ? "#e5e7eb" : "#1e293b" }}>{result.patient_first_name} {result.patient_last_name}</b></span>
      {result.patient_dob && <span>DOB {result.patient_dob}</span>}
      {result.collection_date && <span>Collected {formatEST(result.collection_date, { dateOnly: true })}</span>}
      {physician && <span>Ordered by {physician}</span>}
    </Box>
  );
}

// 2026-10-21 fix (downstream-mismatch-checker finding) —
// abnormal_value_insights is a LIST (ocr_engine.py's
// _sanitize_abnormal_value_insights), not a dict keyed by test name — it
// carries no test-name/identifier field at all. Joined here instead by
// (value, unit, reference_range), the one tuple both this list and
// structured_results.full_results share — best-effort (a genuine
// collision would need two different tests with identical value/unit/
// range on the same report, which the AI generation step draws from the
// same underlying flagged-value data, making a false match very
// unlikely in practice). Not a perfect long-term fix — the real fix
// belongs upstream, having the AI/sanitizer attach a real test-name key
// — but this closes the "explanation feature silently never fires" break
// without requiring a backend/prompt-schema change today.
function insightKey(value, unit, referenceRange) {
  return [value, unit, referenceRange].map((v) => String(v ?? "").trim().toLowerCase()).join("|");
}

// ── Tailored, per-report-type rendering (2026-10-21) ────────────────────
// Every report "kind"/"type" gets its own schema-appropriate section
// instead of one generic layout:
// - "standard" (blood_panel/pcr_panel) — GaugeRow list above, real
//   abnormal/normal, numeric reference range.
// - "tox" / "drug_screen" — categorized Consistent/Inconsistent/Illicit/
//   Validity rows (ToxSection/DrugScreenSection below); no numeric
//   reference range, but a real abnormal/normal-equivalent severity per
//   category (illicit/inconsistent vs. consistent), so still gets the
//   same red/green chip vocabulary.
// - proove_pgx / proove_opioid_risk / proove_pain_perception (stats_json,
//   NOT structured_results) — genuinely no abnormal/normal binary (a
//   gene is never "abnormal", a risk score is never "out of range"), so
//   these get risk-LEVEL-colored interactive tiles/rows instead of a
//   gauge, per direct user correction mid-build.
// Every clickable unit here — a gene row, a score tile, a tox category
// row — funnels into the SAME onOpenValue(row, insight) the lab-value
// gauges already use, so it reuses the existing modal + per-value AI
// chat infrastructure rather than inventing a parallel one per report
// type. `row._statusKey` (new field) lets a non-lab-vocabulary row
// (level/phenotype text) carry its own precomputed severity bucket
// through statusKeyFor's override param.

function SectionHeader({ icon, label, isDark }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
      {icon}
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b" }}>{label}</Typography>
    </Box>
  );
}

function CalloutBlock({ icon, label, text, isDark }) {
  if (!text) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <SectionHeader icon={icon} label={label} isDark={isDark} />
      <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, color: isDark ? "#cbd5e1" : "#334155" }}>{text}</Typography>
    </Box>
  );
}

// Generic clickable row for anything with a label + a severity-bearing
// status string that statusKeyFor's ordinary lab vocabulary already
// understands ("High"/"Low"/"Critical"/"Normal") — used by the tox/
// drug_screen sections, which have real category-level severity but no
// numeric value to gauge.
function ClickableRow({ label, sub, statusText, statusKeyOverride, onOpen, isDark }) {
  const key = statusKeyFor(statusText, statusKeyOverride);
  const color = statusColor(key, isDark);
  return (
    <Box onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5, py: 1, px: 1.5, borderRadius: "10px", cursor: "pointer", minWidth: 0,
        "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.03)" },
        "&:focus-visible": { outline: `2px solid ${BRAND.gold}`, outlineOffset: 2 },
      }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 11.5, color: isDark ? "#94a3b8" : "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {sub}
          </Typography>
        )}
      </Box>
      <Chip size="small" label={STATUS[key]?.label || statusText || "—"} sx={{ fontSize: 11, fontWeight: 700, flexShrink: 0, bgcolor: `${color}1a`, color }} />
    </Box>
  );
}

// A gene/genotype-finding row, clickable into the same modal+chat as a
// lab value. Parameterized so the same component serves PGx's
// {gene, genotype, phenotype, rs_number} shape AND opioid_risk/
// pain_perception's differently-shaped {gene, rs_number, result} shared
// `genotypes` field, without duplicating the row markup twice.
function GeneFindingsList({ items, isDark, onOpenValue, getValue, getPhenotype, getRs }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <Box>
      {items.map((g, i) => {
        const phenotype = getPhenotype(g);
        const key = proveLevelStatusKey(phenotype);
        const color = statusColor(key, isDark);
        const rsNumber = getRs(g);
        const value = getValue(g);
        // 2026-10-21 fix (downstream-mismatch-checker finding) — this used
        // to put the rs-number variant ID into `reference_range`, which
        // the modal/chat-prompt both label as "Reference: ..." — a SNP
        // identifier isn't a reference range. Carried in `unit` instead,
        // parenthesized, so it still displays without being mislabeled.
        // 2026-10-21 fix (verifier finding, CONFIRMED) — rs_number is
        // stored WITH its "rs" prefix already included by every real
        // extraction (e.g. "rs4680", never a bare "4680") — confirmed
        // against the real test fixtures across both repos. Prepending
        // another "rs" here produced "(rsrs4680)" on every gene row that
        // has one. Displayed as stored, not re-prefixed.
        const row = {
          test: g.gene || "Gene", value: value || "—", unit: rsNumber ? `(${rsNumber})` : "",
          reference_range: "—", status: phenotype || "", _statusKey: key,
        };
        return (
          <Box key={i} onClick={() => onOpenValue(row, null)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenValue(row, null); } }}
            sx={{
              display: "flex", alignItems: "center", gap: 1.5, py: 1, px: 1.5, borderRadius: "10px", cursor: "pointer", minWidth: 0,
              "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.03)" },
              "&:focus-visible": { outline: `2px solid ${BRAND.gold}`, outlineOffset: 2 },
            }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b", width: 120, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {g.gene}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: isDark ? "#94a3b8" : "#64748b", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {value || "—"}
            </Typography>
            {/* ui-ux-checker finding — rs_number used to be appended to
                the end of the value string inside one nowrap/ellipsis
                span, so a long genotype/result value clipped the
                rs_number first. Own flexShrink:0 segment instead, so it
                never disappears before the value does. */}
            {/* 2026-10-21 fix (verifier finding, CONFIRMED) — rs_number
                already includes its "rs" prefix in every real record
                (e.g. "rs4680"); this was prepending a second one. */}
            {rsNumber && (
              <Typography sx={{ fontSize: 11.5, color: isDark ? "#64748b" : "#94a3b8", flexShrink: 0 }}>
                {rsNumber}
              </Typography>
            )}
            {phenotype && (
              <Chip size="small" label={phenotype} sx={{ fontSize: 11, fontWeight: 700, bgcolor: `${color}1a`, color, flexShrink: 0 }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// A risk-score/level "dial" tile — the interactive, gauge-equivalent
// device for Proove scores, which have a level/severity but no numeric
// reference range to plot a position against. A lightweight conic-
// gradient ring (not a full ScoreDial.js port — this page's own gauge
// bars are already the load-bearing visual device; this stays
// consistent with that without duplicating SVG dial-drawing code).
function ScoreTile({ label, score, level, onOpen, isDark }) {
  const key = proveLevelStatusKey(level);
  const color = statusColor(key, isDark);
  const pct = key === "high" ? 88 : key === "moderate" ? 55 : key === "indeterminate" ? 50 : 20;
  const track = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,53,122,0.1)";
  return (
    <Box onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      sx={{
        cursor: "pointer", textAlign: "center", width: 132, p: 1.5, borderRadius: 3,
        border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)",
        "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,53,122,0.03)" },
        "&:focus-visible": { outline: `2px solid ${BRAND.gold}`, outlineOffset: 2 },
      }}>
      <Box sx={{
        width: 72, height: 72, borderRadius: "50%", mx: "auto", mb: 1,
        background: `conic-gradient(${color} ${pct}%, ${track} 0)`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: "50%", bgcolor: isDark ? "#111827" : "#fff",
          display: "flex", alignItems: "center", justifyContent: "center", px: 0.5, overflow: "hidden",
        }}>
          {/* ui-ux-checker finding — with no `overflow: hidden` here, a
              multi-word level string (e.g. "No Genetic Predisposition
              Marker", a real value from this app's own level vocabulary)
              wrapped to 3+ lines and bled outside the circular mask. A
              numeric score and a long phrase don't share the same font-
              size/space budget, so the fallback gets its own smaller
              size plus a 3-line clamp instead of just a smaller font. */}
          <Typography sx={score != null ? {
            fontWeight: 800, fontSize: 17, color, lineHeight: 1.1, textAlign: "center",
          } : {
            fontWeight: 700, fontSize: 9, color, lineHeight: 1.15, textAlign: "center",
            display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {score != null ? score : (level || "—")}
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b", mb: 0.5, lineHeight: 1.3 }}>
        {label}
      </Typography>
      {level && (
        <Chip size="small" label={level} sx={{ fontSize: 10.5, fontWeight: 700, bgcolor: `${color}1a`, color, maxWidth: "100%" }} />
      )}
    </Box>
  );
}

// proove_pgx — genes table (every gene, not truncated), flagged
// medications, risk-management findings, and the dosing-guidance
// summary (plain text per the extraction prompt's own instruction, not
// HTML — never dangerouslySetInnerHTML'd).
function PgxSection({ stats, isDark, onOpenValue }) {
  if (!stats) return null;
  const genes = Array.isArray(stats.genes) ? stats.genes : [];
  const riskMgmt = Array.isArray(stats.risk_management) ? stats.risk_management : [];
  const hasAnything = genes.length || riskMgmt.length || stats.current_patient_medications || stats.dosing_guidance_summary;
  if (!hasAnything) {
    return (
      <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#94a3b8", py: 2, textAlign: "center" }}>
        No drug-metabolism findings were extracted from this report.
      </Typography>
    );
  }
  return (
    <Box>
      {genes.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <SectionHeader icon={<Biotech sx={{ fontSize: 16, color: BRAND.gold }} />} label={`Gene findings (${genes.length})`} isDark={isDark} />
          <GeneFindingsList items={genes} isDark={isDark} onOpenValue={onOpenValue}
            getValue={(g) => g.genotype} getPhenotype={(g) => g.phenotype} getRs={(g) => g.rs_number} />
        </Box>
      )}
      <CalloutBlock icon={<Medication sx={{ fontSize: 16, color: BRAND.gold }} />} label="Flagged medications"
        text={stats.current_patient_medications} isDark={isDark} />
      {riskMgmt.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <SectionHeader icon={<WarningAmber sx={{ fontSize: 16, color: BRAND.gold }} />} label="Risk management findings" isDark={isDark} />
          {riskMgmt.map((r, i) => (
            <Box key={i} sx={{ mb: 1.2 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b" }}>{r.condition}</Typography>
              <Typography sx={{ fontSize: 13, color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.55 }}>{r.finding}</Typography>
            </Box>
          ))}
        </Box>
      )}
      <CalloutBlock icon={<Assignment sx={{ fontSize: 16, color: BRAND.gold }} />} label="Dosing guidance summary"
        text={stats.dosing_guidance_summary} isDark={isDark} />
    </Box>
  );
}

// proove_opioid_risk / proove_pain_perception — the score tiles
// (proove_ori/ort/phq2, or self_reported_pain/pss + COMT predisposition).
function ScoreStrip({ reportTypeId, stats, isDark, onOpenValue }) {
  if (!stats) return null;
  const fields = PROOVE_SCORE_LABELS[reportTypeId] || [];
  const present = fields.filter((f) => stats[f.field] && (stats[f.field].score != null || stats[f.field].level));
  const comt = reportTypeId === "proove_pain_perception" ? stats.comt_predisposition : null;
  const hasComt = !!(comt && comt.level);
  if (!present.length && !hasComt) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2.5 }}>
      {present.map((f) => {
        const d = stats[f.field];
        const row = {
          test: f.label, value: d.score != null ? String(d.score) : (d.level || "—"),
          unit: "", reference_range: d.range || "—", status: d.level || "", _statusKey: proveLevelStatusKey(d.level),
        };
        return (
          <ScoreTile key={f.field} label={f.label} score={d.score} level={d.level} isDark={isDark}
            onOpen={() => onOpenValue(row, null)} />
        );
      })}
      {hasComt && (
        <ScoreTile label="Genetic Pain Sensitivity (COMT)" score={null} level={comt.level} isDark={isDark}
          onOpen={() => onOpenValue({
            test: "Genetic Pain Sensitivity (COMT)", value: comt.level, unit: "",
            reference_range: "—", status: comt.level, _statusKey: proveLevelStatusKey(comt.level),
          }, null)} />
      )}
    </Box>
  );
}

// Shared `genotypes` field on opioid_risk/pain_perception — a
// differently-shaped gene list than PGx's own `genes` ({gene, rs_number,
// result} rather than {gene, genotype, phenotype, rs_number}); `result`
// doubles as both the displayed value and the thing classified for the
// status chip, since there's no separate phenotype field here.
function GenotypesSection({ genotypes, isDark, onOpenValue }) {
  if (!Array.isArray(genotypes) || !genotypes.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <SectionHeader icon={<Biotech sx={{ fontSize: 16, color: BRAND.gold }} />} label={`Gene findings (${genotypes.length})`} isDark={isDark} />
      <GeneFindingsList items={genotypes} isDark={isDark} onOpenValue={onOpenValue}
        getValue={(g) => g.result} getPhenotype={(g) => g.result} getRs={(g) => g.rs_number} />
    </Box>
  );
}

function ForeignScoresSection({ scores, isDark, onOpenValue }) {
  if (!Array.isArray(scores) || !scores.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <SectionHeader icon={<Science sx={{ fontSize: 16, color: BRAND.gold }} />} label="Additional scores" isDark={isDark} />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {scores.map((s, i) => {
          const key = proveLevelStatusKey(s?.value);
          const color = statusColor(key, isDark);
          const row = { test: s?.label || "Score", value: s?.value || "—", unit: "", reference_range: "—", status: s?.value || "", _statusKey: key };
          return (
            <Chip key={i} label={`${s?.label || "Score"}: ${s?.value ?? "—"}`} size="small" onClick={() => onOpenValue(row, null)}
              sx={{ fontSize: 11.5, fontWeight: 700, bgcolor: `${color}1a`, color, cursor: "pointer" }} />
          );
        })}
      </Box>
    </Box>
  );
}

// opioid_risk's `history_questions` — personal/family-history phenotype
// questions, read-only (there's no severity to classify, just an answer).
function HistoryQuestionsSection({ items, isDark }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <SectionHeader icon={<Assignment sx={{ fontSize: 16, color: BRAND.gold }} />} label="History questionnaire" isDark={isDark} />
      {items.map((q, i) => (
        <Box key={i} sx={{ mb: 0.8 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: isDark ? "#e5e7eb" : "#1e293b" }}>{q?.question}</Typography>
          <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b" }}>{q?.answer ?? "—"}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// tox_panel section severity — mirrors ocr_engine.py's own
// _TOX_SUMMARY_SECTION_MAP category text (CONSISTENT/INCONSISTENT/
// ILLICIT/NOT TESTED) so the same category reads the same color here as
// it does in the generated PDF. Returns a statusKeyFor-compatible key
// directly (not a lab-vocabulary string) — 2026-10-21 fix (downstream-
// mismatch-checker finding, CONFIRMED): this used to default EVERY
// unmatched category (including "MEDICATIONS PRESCRIBED NOT TESTED FOR")
// to "Normal", so an untested medication rendered as a reassuring green
// chip, the opposite of what "not tested" should communicate. "Not
// tested" now maps to the neutral "indeterminate" bucket, matching
// MySummaries.js's own behavior of never coloring this category at all.
function _toxSeverityKey(section) {
  const s = String(section || "").toUpperCase();
  if (s.includes("ILLICIT")) return "critical";
  if (s.includes("INCONSISTENT")) return "high";
  if (s.includes("NOT TESTED")) return "indeterminate";
  if (s.includes("CONSISTENT")) return "normal";
  return "indeterminate";
}

// tox_panel — categorized Consistent/Inconsistent/Illicit rows
// (first_page_summary + second_section), each clickable into the same
// value modal+chat as a lab value. Deliberately NOT the generic flat
// full_results chip list this replaces — that list had no real category
// context (just "test" + "status/comment"), while these two fields are
// the actual clinically-meaningful tox-summary categorization.
function ToxSection({ structured, isDark, onOpenValue }) {
  const first = Array.isArray(structured?.first_page_summary) ? structured.first_page_summary : [];
  const second = Array.isArray(structured?.second_section) ? structured.second_section : [];
  const rows = [...first, ...second].filter((r) => r && typeof r === "object" && !Array.isArray(r));
  const real = rows.filter((r) => {
    // 2026-10-21 fix (downstream-mismatch-checker finding) — ocr_engine.py's
    // own flatten_tox_summary_first can legitimately default this field to
    // "" (never null), which the old `!= null` check let through as a
    // real row with an empty label — a clickable row with no name, and
    // test:"" sent to /my-results/ask-about-value, which 400s on it.
    const label = r["Drug Prescribed"] ?? r["Analyte Detected"];
    return label != null && String(label).trim() !== "" && label !== "N/A";
  });
  if (!real.length) {
    return (
      <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#94a3b8", py: 2, textAlign: "center" }}>
        No structured values on this report.
      </Typography>
    );
  }
  // ui-ux-checker finding — this used to repeat the full Section
  // sentence as a `sub` line under every single row (a wall of
  // identical text on a report with many rows in the same category,
  // carrying no information beyond what the color-coded chip already
  // conveys). Grouped by category instead — one header, rows underneath.
  const groups = [];
  for (const r of real) {
    const section = r.Section || "Results";
    let g = groups.find((x) => x.section === section);
    if (!g) { g = { section, rows: [] }; groups.push(g); }
    g.rows.push(r);
  }
  return (
    <Box>
      {groups.map((g, gi) => (
        <Box key={gi} sx={{ mb: gi < groups.length - 1 ? 2 : 0 }}>
          <SectionHeader label={g.section} isDark={isDark} />
          {g.rows.map((r, i) => {
            const label = String(r["Drug Prescribed"] ?? r["Analyte Detected"] ?? "—");
            const key = _toxSeverityKey(r.Section);
            const row = {
              test: label, value: r["Result (ng/mL)"] || "—", unit: "",
              reference_range: r["Cutoff (ng/mL)"] || "—", status: r.Section || "", _statusKey: key,
            };
            return (
              <ClickableRow key={i} label={label} statusKeyOverride={key}
                onOpen={() => onOpenValue(row, null)} isDark={isDark} />
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

// drug_screen — consistent/inconsistent medication rows plus a separate
// specimen-validity section (dilute/substituted/adulterated flags),
// each clickable the same way. `__EMPTY__` is flatten_drug_screen_
// consistent's own "nothing in this category" sentinel row — filtered
// out here rather than rendered as a fake clickable drug.
function DrugScreenSection({ structured, isDark, onOpenValue }) {
  const consistent = Array.isArray(structured?.consistent)
    ? structured.consistent.filter((r) => r && typeof r === "object" && r.Drug && r.Drug !== "__EMPTY__")
    : [];
  // 2026-10-21 fix (downstream-mismatch-checker finding) — a row missing
  // "Validity Test" would otherwise pass through as a clickable row with
  // no name, sending test:"" to /my-results/ask-about-value (400s there).
  const validity = (Array.isArray(structured?.validity) ? structured.validity : [])
    .filter((v) => v && typeof v === "object" && v["Validity Test"] && String(v["Validity Test"]).trim() !== "");
  if (!consistent.length && !validity.length) {
    return (
      <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#94a3b8", py: 2, textAlign: "center" }}>
        No structured values on this report.
      </Typography>
    );
  }
  const consistentGroups = [];
  for (const r of consistent) {
    const section = r.Section || "Results";
    let g = consistentGroups.find((x) => x.section === section);
    if (!g) { g = { section, rows: [] }; consistentGroups.push(g); }
    g.rows.push(r);
  }
  return (
    <Box>
      {consistentGroups.map((g, gi) => (
        <Box key={gi} sx={{ mb: 1.5 }}>
          <SectionHeader label={g.section} isDark={isDark} />
          {g.rows.map((r, i) => {
            const statusText = String(r.Section || "").toUpperCase().includes("INCONSISTENT") ? "High" : "Normal";
            const row = { test: r.Drug, value: r.Result || "—", unit: "", reference_range: r.Cutoff || "—", status: statusText };
            return (
              <ClickableRow key={`c-${gi}-${i}`} label={r.Drug} statusText={statusText}
                onOpen={() => onOpenValue(row, null)} isDark={isDark} />
            );
          })}
        </Box>
      ))}
      {validity.length > 0 && (
        <Box sx={{ mt: consistent.length ? 2 : 0, pt: consistent.length ? 2 : 0, borderTop: consistent.length ? (isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.06)") : "none" }}>
          <SectionHeader icon={<Science sx={{ fontSize: 16, color: BRAND.gold }} />} label="Specimen validity" isDark={isDark} />
          {validity.map((v, i) => {
            const statusText = v.Flagged ? "High" : "Normal";
            const row = { test: v["Validity Test"], value: v["Test Outcome"] || "—", unit: "", reference_range: v["Reference Range"] || "—", status: statusText };
            return (
              <ClickableRow key={`v-${i}`} label={v["Validity Test"]} statusText={statusText}
                onOpen={() => onOpenValue(row, null)} isDark={isDark} />
            );
          })}
        </Box>
      )}
    </Box>
  );
}

const _PROOVE_TYPED_IDS = new Set(["proove_pgx", "proove_opioid_risk", "proove_pain_perception"]);

function stripHtml(html) {
  return String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

// Stage 3, whole-report chat — a short list of plain-text highlight
// strings covering whatever this specific report actually has, built
// the same per-report-type-aware way ResultCard itself renders (flagged
// lab values for "standard", categorized tox/drug_screen findings, gene/
// score findings for a Proove-typed report). Sent to the backend as-is
// rather than having the backend re-derive report shape from raw
// stats_json/structured_results — keeps this in one place, next to the
// rendering logic it has to stay consistent with.
// 2026-10-21 fix (verifier finding, CONFIRMED by a real repro run) —
// every list here used `(x || [])`, which only guards a falsy value
// (null/undefined); a truthy NON-array (e.g. stats_json.genes stored as
// `{}` from a malformed/legacy/partially-migrated document) still threw
// "is not iterable" inside a `for...of`, called directly during render
// (`useMemo`) — crashing the whole modal open, not degrading to an
// empty list as every sibling render component in this same file
// (PgxSection/GenotypesSection/ForeignScoresSection/ToxSection/
// DrugScreenSection) already correctly does via `Array.isArray`. Every
// list below now uses the same guard those components use.
function _asArray(x) {
  return Array.isArray(x) ? x : [];
}

function buildReportHighlights(result) {
  const structured = result.structured_results;
  const stats = result.stats_json;
  const rtid = result.report_type_id;
  const out = [];
  if (_PROOVE_TYPED_IDS.has(rtid) && stats) {
    if (rtid === "proove_pgx") {
      for (const g of _asArray(stats.genes)) {
        if (g && g.gene) out.push(`${g.gene}: ${g.genotype || "—"}${g.phenotype ? ` (${g.phenotype})` : ""}`);
      }
      // 2026-10-21 addition (verifier finding) — current_patient_
      // medications/dosing_guidance_summary are rendered in PgxSection
      // (ResultCard's own sibling display) but were missing here despite
      // this function's own header comment claiming full parity with
      // that rendering; flagged medications in particular is exactly the
      // kind of content a patient would want to discuss in this chat.
      if (stats.current_patient_medications) out.push(`Flagged medications: ${stats.current_patient_medications}`);
      for (const r of _asArray(stats.risk_management)) {
        if (r && r.condition) out.push(`${r.condition}: ${r.finding || ""}`);
      }
      if (stats.dosing_guidance_summary) out.push(`Dosing guidance: ${stats.dosing_guidance_summary}`);
    } else {
      const fields = PROOVE_SCORE_LABELS[rtid] || [];
      for (const f of fields) {
        const d = stats[f.field];
        if (d && (d.score != null || d.level)) {
          out.push(`${f.label}: ${d.score != null ? d.score : ""}${d.level ? ` (${d.level})` : ""}`.trim());
        }
      }
      if (rtid === "proove_pain_perception" && stats.comt_predisposition?.level) {
        out.push(`Genetic Pain Sensitivity (COMT): ${stats.comt_predisposition.level}`);
      }
      for (const g of _asArray(stats.genotypes)) {
        if (g && g.gene) out.push(`${g.gene}: ${g.result || "—"}`);
      }
      for (const s of _asArray(stats.foreign_scores)) {
        if (s && s.label) out.push(`${s.label}: ${s.value ?? "—"}`);
      }
      // 2026-10-21 addition (verifier finding) — history_questions is
      // rendered in HistoryQuestionsSection (opioid_risk only) but was
      // missing here for the same claimed-parity reason as above.
      if (rtid === "proove_opioid_risk") {
        for (const q of _asArray(stats.history_questions)) {
          if (q && q.question) out.push(`${q.question}: ${q.answer ?? "—"}`);
        }
      }
    }
  } else if (structured && structured.kind === "standard") {
    for (const row of _asArray(structured.full_results)) {
      if (row && statusKeyFor(row.status) !== "normal") {
        out.push(`${row.test}: ${row.value || ""} ${row.unit || ""} (${row.status})`.replace(/\s+/g, " ").trim());
      }
    }
  } else if (structured && structured.kind === "tox") {
    const rows = [..._asArray(structured.first_page_summary), ..._asArray(structured.second_section)];
    for (const r of rows) {
      const label = r?.["Drug Prescribed"] ?? r?.["Analyte Detected"];
      if (label != null && String(label).trim() !== "" && label !== "N/A") {
        out.push(`${label}: ${r["Result (ng/mL)"] || "—"} (${r.Section || ""})`.trim());
      }
    }
  } else if (structured && structured.kind === "drug_screen") {
    for (const r of _asArray(structured.consistent)) {
      if (r && r.Drug && r.Drug !== "__EMPTY__") out.push(`${r.Drug}: ${r.Result || "—"} (${r.Section || ""})`.trim());
    }
    for (const v of _asArray(structured.validity)) {
      if (v && v["Validity Test"]) out.push(`${v["Validity Test"]}: ${v["Test Outcome"] || "—"}${v.Flagged ? " (Flagged)" : ""}`);
    }
  }
  return out;
}

// Whole-report chat box — same multi-turn-within-session, never-
// persisted design as ValueChatBox above, scoped to an entire report
// instead of one value. Deliberately duplicated rather than parameterized
// into ValueChatBox: the two POST different shapes (value_context vs.
// report_context) to two different endpoints, and sharing one component
// would mean branching nearly every prop/handler — not worth the
// indirection for this much code.
function ReportChatBox({ result, isDark }) {
  const { user } = useContext(UserContext);
  const token = user?.token;
  const [messages, setMessages] = useState([]); // [{question, answer}]
  const [question, setQuestion] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const inputRef = React.useRef(null);
  const bottomRef = React.useRef(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages]);

  const highlights = useMemo(() => buildReportHighlights(result), [result]);

  const send = async () => {
    const q = question.trim();
    if (!q || sending) return;
    setSending(true);
    setError("");
    try {
      const res = await axios.post(`${API_BASE}/my-results/ask-about-report`,
        { question: q, history: messages, report_context: {
          report_type_id: result.report_type_id, generated_at: result.generated_at,
          summary: stripHtml(result.ai_summary), highlights,
        } },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // 2026-10-24 fix (carried-forward finding #2, flow audit) — same
      // fix as /my-results/ask-about-value's own identical gap.
      if (res.data?.feature_disabled) {
        setError("This feature isn't available yet.");
        return;
      }
      setMessages((prev) => [...prev, { question: q, answer: res.data.answer }]);
      setQuestion("");
    } catch (err) {
      setError(extractErrorDetail(err, "Couldn't get an answer right now — please try again."));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, color: isDark ? "#64748b" : "#94a3b8", mb: 1.2, lineHeight: 1.5 }}>
        Answers are generated by AI based on this one report — not a substitute for advice from your provider.
      </Typography>
      {messages.length > 0 && (
        <Box sx={{ mb: 1.5, display: "flex", flexDirection: "column", gap: 1.2 }}>
          {messages.map((m, i) => (
            <Box key={i}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b" }}>{m.question}</Typography>
              <Box sx={{
                fontSize: 13.5, color: isDark ? "#94a3b8" : "#64748b", mt: 0.3, lineHeight: 1.55,
                "& p": { m: 0, mb: 0.8 }, "& ul": { m: 0, pl: 2.5 },
              }}>
                <ReactMarkdown>{m.answer}</ReactMarkdown>
              </Box>
            </Box>
          ))}
          <div ref={bottomRef} />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, fontSize: 13 }}>{error}</Alert>}
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField size="small" fullWidth placeholder="e.g. What should I ask my doctor about this?" value={question}
          inputRef={inputRef}
          slotProps={{ input: { readOnly: sending } }}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <IconButton onClick={send} disabled={sending || !question.trim()} aria-label="Send question"
          sx={{ color: BRAND.navy, "&:hover": { bgcolor: "rgba(0,53,122,0.08)" } }}>
          {sending ? <CircularProgress size={18} sx={{ color: BRAND.navy }} /> : <Send fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );
}

// Whole-report chat modal — opened from a button on the card itself
// (not navigation into the real Aiden chat page, which has no backend
// capability today to load an already-stored report without
// re-uploading a PDF; see the plan doc's own note on this fork). Kept
// simple (open/close boolean, no last-value ref trick) since there's no
// null-content-during-close-animation risk here — the modal's content
// doesn't depend on anything that goes null when it closes.
function ReportChatModal({ open, onClose, result, isDark }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, bgcolor: isDark ? "#111827" : "#fff" } } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SmartToy sx={{ fontSize: 20, color: BRAND.gold }} />
          <Typography sx={{ fontWeight: 800, fontSize: 17, color: isDark ? "#f1f5f9" : BRAND.navy }}>
            Discuss this report
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: isDark ? "#94a3b8" : "#64748b" }}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <ReportChatBox result={result} isDark={isDark} />
      </DialogContent>
    </Dialog>
  );
}

function ResultCard({ result, showAbnormalOnly, isDark, onOpenValue }) {
  const structured = result.structured_results;
  const stats = result.stats_json;
  const rtid = result.report_type_id;
  const isProoveTyped = _PROOVE_TYPED_IDS.has(rtid);
  // Stage 3, whole-report chat — local to this card (not lifted to the
  // page-level `detail` state the per-value modal uses), since only this
  // card's own button opens it and nothing else on the page needs to
  // know it's open.
  const [reportChatOpen, setReportChatOpen] = useState(false);
  const insightsByKey = useMemo(() => {
    const map = {};
    for (const insight of result.abnormal_value_insights || []) {
      map[insightKey(insight.value, insight.unit, insight.reference_range)] = insight;
    }
    return map;
  }, [result.abnormal_value_insights]);
  const rows = useMemo(() => {
    if (!structured || structured.kind !== "standard") return [];
    const all = structured.full_results || [];
    return showAbnormalOnly ? all.filter((r) => statusKeyFor(r.status) !== "normal") : all;
  }, [structured, showAbnormalOnly]);

  return (
    <Box sx={{
      borderRadius: 3, overflow: "hidden", mb: 3,
      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.1)",
      bgcolor: isDark ? "#111827" : "#fff",
      boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,53,122,0.06)",
    }}>
      {/* 2026-10-21 fix (ui-ux-checker finding) — this page had no card
          header at all, unlike MySummaries.js's established gradient
          header bar (icon + report type + date chip). With more than one
          report on the page, there was no way to tell which test a card
          belonged to without reading the test names inside it. Same
          convention reused here, not reinvented — patient-only variant
          only (clinician-flavor rows never reach this page at all). */}
      <Box sx={{
        px: { xs: 2, sm: 3 }, py: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
        background: `linear-gradient(135deg, ${BRAND.navyDark} 0%, ${BRAND.navy} 100%)`,
      }}>
        <PersonOutline sx={{ color: BRAND.gold, fontSize: 18 }} />
        <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: 14.5, textTransform: "capitalize" }}>
          {String(result.report_type_id || "").replace(/_/g, " ")}
        </Typography>
        {stats?.is_proove_branded && (
          <Chip label="Proove Genomics" size="small"
            sx={{ bgcolor: "rgba(201,169,110,0.28)", color: "#fff", fontSize: "0.62rem", fontWeight: 700 }} />
        )}
        {result.provider_name && (
          // Ordering clinician's name + practice, same right-side header
          // placement as MySummaries.js's own "Ordered by" addition
          // (2026-10-24, user-directed) — both fields are already present
          // on this route's response (never stripped, deliberately kept
          // per main.py's own "directory-style public-record info"
          // convention); NPI and provider_phone are deliberately never
          // shown to the patient.
          <Chip label={`Ordered by: ${result.provider_name}${result.practice_name ? ` — ${result.practice_name}` : ""}`} size="small"
            sx={{ ml: "auto", bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontSize: "0.68rem" }} />
        )}
        {result.generated_at && (
          <Chip label={formatEST(result.generated_at, { dateOnly: true })} size="small"
            sx={{ ml: result.provider_name ? 0 : "auto", bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontSize: "0.68rem" }} />
        )}
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <DemographicsStrip result={result} isDark={isDark} />

      {result.ai_summary && (
        <Box sx={{ mb: 2.5, fontSize: 14, lineHeight: 1.65, color: isDark ? "#cbd5e1" : "#334155", "& p": { mb: 1.5 }, "& ul": { pl: 3 } }}
          dangerouslySetInnerHTML={{ __html: result.ai_summary }} />
      )}

      {isProoveTyped && result.stats_extraction_status === "pending" ? (
        // 2026-10-21 fix (downstream-mismatch-checker finding, CONFIRMED)
        // — MySummaries.js has a mandatory "still generating" state for
        // exactly this backend condition (its own IdentityHeader/DialRow
        // area, gated the same way). This page had none — dispatch here
        // is purely on report_type_id, so a Proove report whose stats
        // extraction hasn't finished yet rendered as a seemingly broken,
        // completely empty findings area instead of this same affordance.
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 2, color: isDark ? "#94a3b8" : "#64748b" }}>
          <HourglassEmpty sx={{ fontSize: 20, opacity: 0.7 }} />
          <Typography sx={{ fontStyle: "italic", fontSize: "0.9rem" }}>
            Your results are still being generated — check back shortly.
          </Typography>
        </Box>
      ) : isProoveTyped ? (
        // Proove-typed reports (stats_json, not structured_results) —
        // genuinely no abnormal/normal binary, so these render as
        // risk-level-colored interactive tiles/rows instead of a gauge,
        // per direct user correction mid-build (2026-10-21).
        rtid === "proove_pgx" ? (
          <PgxSection stats={stats} isDark={isDark} onOpenValue={onOpenValue} />
        ) : (
          <>
            <ScoreStrip reportTypeId={rtid} stats={stats} isDark={isDark} onOpenValue={onOpenValue} />
            <GenotypesSection genotypes={stats?.genotypes} isDark={isDark} onOpenValue={onOpenValue} />
            <ForeignScoresSection scores={stats?.foreign_scores} isDark={isDark} onOpenValue={onOpenValue} />
            {rtid === "proove_opioid_risk" && <HistoryQuestionsSection items={stats?.history_questions} isDark={isDark} />}
          </>
        )
      ) : structured && structured.kind === "standard" ? (
        rows.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#94a3b8", py: 2, textAlign: "center" }}>
            {showAbnormalOnly ? "No abnormal values in this report." : "No structured values on this report."}
          </Typography>
        ) : (
          <Box>
            {rows.map((row, i) => (
              <GaugeRow key={`${row.test}-${i}`} row={row}
                insight={insightsByKey[insightKey(row.value, row.unit, row.reference_range)]}
                isDark={isDark} onOpen={onOpenValue} />
            ))}
          </Box>
        )
      ) : structured && structured.kind === "tox" ? (
        <ToxSection structured={structured} isDark={isDark} onOpenValue={onOpenValue} />
      ) : structured && structured.kind === "drug_screen" ? (
        <DrugScreenSection structured={structured} isDark={isDark} onOpenValue={onOpenValue} />
      ) : null}

      {Array.isArray(result.followup_test_suggestions) && result.followup_test_suggestions.length > 0 && (
        <Box sx={{ mt: 2.5, pt: 2, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.06)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
            <Assignment sx={{ fontSize: 16, color: BRAND.gold }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b" }}>Follow-up test suggestions</Typography>
          </Box>
          {result.followup_test_suggestions.map((s, i) => (
            <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", ml: 0.3, mb: 0.3 }}>
              • <b>{displayNameForTestId(s.test_id)}</b>{s.rationale ? ` — ${s.rationale}` : ""}
            </Typography>
          ))}
        </Box>
      )}

      {result.lifestyle_html && (
        <Box sx={{ mt: 2.5, pt: 2, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.06)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
            <LocalFlorist sx={{ fontSize: 16, color: BRAND.gold }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b" }}>Lifestyle & wellness</Typography>
          </Box>
          <Box sx={{ fontSize: 13.5, lineHeight: 1.6, color: isDark ? "#cbd5e1" : "#334155", "& p": { mb: 1.2 }, "& ul": { pl: 3 } }}
            dangerouslySetInnerHTML={{ __html: result.lifestyle_html }} />
        </Box>
      )}

      {/* Stage 3, second entry point — see MY_RESULTS_ECOSYSTEM_PLAN.md
          §5 Stage 3 point 2. A self-contained chat (not navigation into
          the real Aiden chat page — it has no backend capability today
          to load an already-stored report without re-uploading a PDF). */}
      <Box sx={{ mt: 2.5, pt: 2, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.06)", textAlign: "center" }}>
        <Box onClick={() => setReportChatOpen(true)} role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setReportChatOpen(true); } }}
          sx={{
            display: "inline-flex", alignItems: "center", gap: 0.8, px: 2, py: 1, borderRadius: "20px", cursor: "pointer",
            border: isDark ? "1px solid rgba(201,169,110,0.35)" : "1px solid rgba(201,169,110,0.5)",
            color: isDark ? BRAND.gold : BRAND.navy, fontSize: 13, fontWeight: 700,
            "&:hover": { bgcolor: isDark ? "rgba(201,169,110,0.08)" : "rgba(201,169,110,0.1)" },
            "&:focus-visible": { outline: `2px solid ${BRAND.gold}`, outlineOffset: 2 },
          }}>
          <SmartToy sx={{ fontSize: 17 }} />
          Discuss this report with Aiden
        </Box>
      </Box>
      <ReportChatModal open={reportChatOpen} onClose={() => setReportChatOpen(false)} result={result} isDark={isDark} />
      </Box>
    </Box>
  );
}

export default function MyResults() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const token = user?.token;
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  // 2026-10-21 fix (ui-ux-checker finding) — "the feature isn't turned
  // on yet" (benign, expected) and "something genuinely failed to load"
  // were both rendered through the same neutral info-blue Alert, a
  // tone-deaf mismatch for a real failure. Tracked as a typed state
  // instead of a single message string so the two render with different
  // severity.
  const [errorState, setErrorState] = useState(null); // { message, severity } | null
  const [showAbnormalOnly, setShowAbnormalOnly] = useState(false);
  const [detail, setDetail] = useState(null); // { row, insight } | null
  // "My Results" ecosystem plan, Phase 1, Stage 2 — trends data, fetched
  // alongside the main results list. Best-effort: a trends-fetch failure
  // never blocks the core page (that's the one thing this page actually
  // can't work without); it just means no overview/sparklines render.
  const [trends, setTrends] = useState({ charts: [], visit_count: 0 });

  useEffect(() => {
    if (!MY_RESULTS_ENABLED) {
      setErrorState({ message: "My Results isn't available yet.", severity: "info" });
      setLoading(false);
      return;
    }
    // 2026-10-21 fix (ui-ux-checker finding, real layout-shift bug) —
    // these two fetches used to resolve independently; since the trends
    // fetch has no loading state of its own, it was possible for the
    // main results list to paint first and the trends grid to pop in
    // afterward, shoving the already-read list down the page. Both are
    // now awaited together under the SAME `loading` flag — the page
    // either shows its loading spinner or shows everything at once,
    // never a partial render that a later arrival then disrupts.
    const resultsPromise = axios.get(`${API_BASE}/my-results`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.data?.feature_disabled) {
          setErrorState({ message: "My Results isn't available yet.", severity: "info" });
          return;
        }
        setResults(res.data.results || []);
      })
      .catch(() => setErrorState({ message: "Unable to load your results right now.", severity: "error" }));
    const trendsPromise = axios.get(`${API_BASE}/my-results/trends`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (!res.data?.feature_disabled) setTrends(res.data); })
      .catch(() => {}); // best-effort — a trends-fetch failure never blocks the core page
    Promise.allSettled([resultsPromise, trendsPromise]).then(() => setLoading(false));
  }, [token]);

  const openValue = (row, insight) => setDetail({ row, insight });
  // Stage 2 — this row's own trend chart data, if it has 2+ history
  // points (the same inclusion rule the backend already applied). Looked
  // up by test name directly (unlike abnormal_value_insights, the trends
  // aggregation already keys by test name server-side — no join-by-value
  // workaround needed here).
  // 2026-10-21 fix (self-caught regression from the backend's own
  // _canon_test fix) — /my-results/trends now canonicalizes test names
  // (e.g. "egfr" -> "EGFR NON AFRICAN AMER", the exact alias table the
  // sibling /trends/patient_bot route already used) so the same analyte
  // merges into one real trend across visits instead of silently
  // fragmenting. But THIS report's own row.test (from /my-results,
  // never canonicalized — it shows the lab's own original label) can now
  // legitimately differ from the canonicalized c.test for the ~20 known
  // aliases, which would make an exact-match join miss a real trend that
  // does exist. A case-insensitive fallback catches plain case drift;
  // the small set of genuine text-rewriting aliases (e.g. "egfr" vs the
  // full canonical name) is a known, accepted residual gap — porting
  // that alias table into JS as well isn't worth the duplicate-
  // maintenance cost for this narrow a case.
  const trendChartFor = (row) => {
    const rowTest = String(row.test || "").trim().toLowerCase();
    return trends.charts.find((c) => c.test === row.test)
      || trends.charts.find((c) => c.test.trim().toLowerCase() === rowTest)
      || null;
  };

  return (
    // 2026-10-21 fix (ui-ux-checker finding) — TrendsPanel.js's own
    // motion.div card-entrance animations (reused here, see the Stage 2
    // overview section below) only honor prefers-reduced-motion when a
    // MotionConfig ancestor opts into it — BotInterface.js (its other
    // mount point) already wraps itself this way; this page had no such
    // wrapper at all, so the stagger-fade ran unconditionally regardless
    // of the user's OS motion preference.
    <MotionConfig reducedMotion="user">
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f8f6f2" }}>
      <Box sx={{
        px: 3, py: 2, bgcolor: isDark ? "#111827" : "#fff",
        borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)",
        display: "flex", alignItems: "center", gap: 2,
      }}>
        <Box onClick={() => navigate("/home")} role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/home"); } }}
          sx={{
            display: "flex", alignItems: "center", gap: 0.5,
            color: isDark ? "#94a3b8" : "#64748b", fontSize: 14.5, cursor: "pointer",
            borderRadius: "8px", px: 1.2, py: 0.6, transition: "all 0.2s",
            "&:hover": { color: isDark ? BRAND.gold : BRAND.navy, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.06)" },
            "&:focus-visible": { outline: `2px solid ${BRAND.gold}`, outlineOffset: 2 },
          }}
        >
          <ArrowBack sx={{ fontSize: 15 }} />
          <span>Home</span>
        </Box>
        <Box sx={{ width: "1px", height: 20, bgcolor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,53,122,0.1)" }} />
        <Typography sx={{ fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, fontSize: 18, flex: 1 }}>My Results</Typography>
        <ThemeToggle />
        <NavUserMenu />
      </Box>

      <Box sx={{ maxWidth: 820, mx: "auto", px: 3, py: 5 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 4 }}>
          <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", maxWidth: 480 }}>
            Every value from your completed tests, shown against its own reference range.
          </Typography>
          {results.length > 0 && (
            <FormControlLabel
              control={<Switch size="small" checked={showAbnormalOnly} onChange={(e) => setShowAbnormalOnly(e.target.checked)}
                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND.gold }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: BRAND.gold } }} />}
              label={<Typography sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155" }}>Show abnormal only</Typography>}
              sx={{ m: 0 }}
            />
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND.navy }} /></Box>
        ) : errorState ? (
          <Alert severity={errorState.severity} sx={{ borderRadius: 2 }}>{errorState.message}</Alert>
        ) : results.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
            <Science sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
            <Typography>No results yet — check back once your first test is complete.</Typography>
          </Box>
        ) : (
          <>
            {/* Stage 2 — "All Values Trends" overview, positioned above
                the per-report cards per the plan doc. Reuses TrendsPanel.js
                UNCHANGED (same component BotInterface.js already mounts),
                just pointed at this patient's own report history instead
                of chatbot visit history. Covers every tracked value with
                2+ history points, not only currently-abnormal ones. */}
            {trends.charts.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <TrendsPanel charts={trends.charts} visitCount={trends.visit_count} isDark={isDark}
                  border={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.08)"}
                  textPrimary={isDark ? "#f1f5f9" : "#1e293b"}
                  textSecondary={isDark ? "#94a3b8" : "#64748b"}
                  // 2026-10-21 fix (ui-ux-checker finding, trust-relevant)
                  // — without this, the same value could read red in the
                  // gauge-bar list below and amber in this chart above it
                  // on the same page. Matched to this page's own
                  // statusColor() so the signal always agrees.
                  statusColor={{ High: statusColor("high", isDark), Low: statusColor("low", isDark), Normal: statusColor("normal", isDark) }} />
              </Box>
            )}
          {results.map((r) => (
            <ResultCard key={r._id} result={r} showAbnormalOnly={showAbnormalOnly} isDark={isDark}
              onOpenValue={openValue} />
          ))}
          </>
        )}
      </Box>

      <ValueDetailModal
        open={!!detail} onClose={() => setDetail(null)}
        row={detail?.row} insight={detail?.insight} isDark={isDark}
        trendChart={detail?.row ? trendChartFor(detail.row) : null}
      />
    </Box>
    </MotionConfig>
  );
}
